<template>
  <div class="streak-achievement-modal">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="loading-spinner">🔥</div>
      <p>{{ t('streak.loading-achievement') }}</p>
    </div>

    <!-- Achievement Content -->
    <template v-else>
      <!-- Header Section -->

      <!-- Streak Display -->
      <div class="streak-display">
        <div class="streak-number-container">
          <div class="streak-number">{{ currentStreak }}</div>
          <div class="streak-label">Days</div>
        </div>
        <div class="streak-flame">🔥</div>
      </div>

      <!-- Achievement Message -->
      <div class="achievement-message">
        <h2 class="congrats-text">{{ congratsMessage }}</h2>
        <p class="motivational-text">{{ motivationalMessage }}</p>
      </div>

      <!-- Dynamic Stats Section -->
      <div class="stats-section" v-if="todayStats.length > 0">
        <div 
          v-for="(stat, index) in todayStats" 
          :key="stat.label"
          class="stat-item"
          :style="{ '--delay': index * 0.1 + 's' }"
        >
          <div class="stat-content">
            <div class="stat-icon">{{ stat.icon }}</div>
            <div class="stat-text">
              <div class="stat-value">{{ stat.value }}</div>
              <div class="stat-label">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Action -->
      <div class="modal-footer">
        <button type="button" class="btn btn-celebrate" @click="close">
          Keep the fire burning!
        </button>
      </div>
    </template>
  </div>
</template>
<script setup>
import { ref, onMounted, computed } from 'vue'
import { fetchWrapper } from '@/helpers/fetch-wrapper'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Props
const props = defineProps({
  debug: { type: Boolean, default: false }
})

// Emit events
const emit = defineEmits(['close'])

// Reactive data
const currentStreak = ref(0)
const today = ref({})
const month = ref({})
const isLoading = ref(true)

const fetchAchievementsData = async () => {
  if (props.debug) {
    // Return static data for testing
    return {
      current_streak: 7,
      today: {
        xp: 150,
        days_studied: 1,
        words_mastered: 12,
        flashcards_reviewed: 25,
        messages_sent: 8,
        topics_completed: 2
      },
      month: {
        xp: 1250,
        days_studied: 15,
        words_mastered: 180,
        flashcards_reviewed: 450,
        messages_sent: 120,
        topics_completed: 28
      }
    }
  } else {
    // Fetch data from the API
    return await fetchWrapper.get('/achievements/')
  }
}

onMounted(async () => {
  try {
    const response = await fetchAchievementsData()
    currentStreak.value = response.current_streak
    today.value = response.today
    month.value = response.month
    isLoading.value = false
  } catch (error) {
    console.error('Failed to fetch achievements data:', error)
    isLoading.value = false
  }
})

// Dynamic congratulatory messages based on streak length
const congratsMessage = computed(() => {
  const count = currentStreak.value
  if (count === 1) return t('achievements.first-day-complete')
  if (count <= 3) return t('achievements.great-start')
  if (count <= 7) return t('achievements.building-momentum')
  if (count <= 14) return t('achievements.youre-on-fire')
  if (count <= 30) return t('achievements.unstoppable-streak')
  if (count <= 50) return t('achievements.dedication-master')
  if (count <= 100) return t('achievements.legendary-learner')
  return t('achievements.beyond-amazing')
})

// Motivational messages
const motivationalMessage = computed(() => {
  const messages = [
    t('achievements.consistency-is-the-key-to-mastery'),
    t('achievements.every-day-counts—keep-going'),
    t('achievements.be-proud-of-your-progress'),
    t('achievements.your-language-journey-is-inspiring'),
    t('achievements.small-steps-lead-to-big-dreams')
  ]
  return messages[Math.floor(Math.random() * messages.length)]
})

// Get today's activity stats for display
const todayStats = computed(() => [
  {
    icon: '📚',
    value: today.value.words_mastered || 0,
    label: t('achievements.words-mastered')
  },
  {
    icon: '⚡',
    value: today.value.xp || 0,
    label: t('achievements.xp')
  },
  {
    icon: '💬',
    value: today.value.messages_sent || 0,
    label: t('achievements.messages-sent')
  },
  {
    icon: '🎯',
    value: today.value.flashcards_reviewed || 0,
    label: t('achievements.flashcards-reviewed')
  },
  {
    icon: '🏆',
    value: today.value.topics_completed || 0,
    label: t('achievements.topics-completed')
  }
].filter(stat => stat.value > 0)) // Show top 3 non-zero stats

const close = () => {
  emit('close')
}
</script>

<style scoped>
.streak-achievement-modal {
  text-align: center;
  padding: 1.0rem;
  background: white;
  color: #333;
  max-width: 500px;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  min-height: unset;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.loading-spinner {
  font-size: 4rem;
  animation: spin 2s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.streak-achievement-modal::before {
  content: '';
  position: absolute;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: pulse 3s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.1); opacity: 0.8; }
}

.achievement-header {
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
}

.streak-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
  animation: bounce 1s ease-in-out infinite alternate;
}

@keyframes bounce {
  from { transform: translateY(0px); }
  to { transform: translateY(-10px); }
}

.achievement-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.25rem;
  text-shadow: none;
  color: #333;
  animation: slideInDown 0.8s ease-out;
}

@keyframes slideInDown {
  from { transform: translateY(-30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.streak-subtitle {
  font-size: 1rem;
  opacity: 0.7;
  color: #666;
  animation: fadeIn 1s ease-out 0.5s both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.streak-display {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem 0;
  position: relative;
  z-index: 1;
}

.streak-number-container {
  background: rgba(102, 126, 234, 0.1);
  border-radius: 50%;
  padding: 1.5rem;
  margin-right: 0.75rem;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(102, 126, 234, 0.2);
  animation: scaleIn 0.8s ease-out 0.3s both;
}

@keyframes scaleIn {
  from { transform: scale(0.5); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.streak-number {
  font-size: 2.5rem;
  font-weight: bold;
  line-height: 1;
  color: #667eea;
  text-shadow: none;
}

.streak-label {
  font-size: 0.9rem;
  font-weight: 500;
  margin-top: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #666;
}

.streak-flame {
  font-size: 2.5rem;
  animation: flicker 1.5s ease-in-out infinite alternate;
}

@keyframes flicker {
  0%, 100% { transform: scale(1) rotate(-5deg); }
  50% { transform: scale(1.1) rotate(5deg); }
}

.achievement-message {
  margin: 1rem 0;
  position: relative;
  z-index: 1;
}

.congrats-text {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: #333;
  text-shadow: none;
  animation: slideInUp 0.8s ease-out 0.7s both;
}

@keyframes slideInUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.motivational-text {
  font-size: 1rem;
  opacity: 0.8;
  color: #666;
  line-height: 1.4;
  animation: fadeIn 1s ease-out 1s both;
}

.stats-section {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin: 1rem 0;
  position: relative;
  z-index: 1;
}

.stat-item {
  background: rgba(102, 126, 234, 0.08);
  border-radius: 12px;
  padding: 0.6rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(102, 126, 234, 0.15);
  width: 100%;
  animation: fadeInUp 0.8s ease-out calc(1.2s + var(--delay, 0s)) both;
  text-align: left;
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.stat-text {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex: 1;
}

.stat-item:nth-child(1) { --delay: 0s; }
.stat-item:nth-child(2) { --delay: 0.1s; }
.stat-item:nth-child(3) { --delay: 0.2s; }
.stat-item:nth-child(4) { --delay: 0.3s; }
.stat-item:nth-child(5) { --delay: 0.4s; }

@keyframes fadeInUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.stat-icon {
  font-size: 1.25rem;
  margin-bottom: 0;
  flex-shrink: 0;
}

.stat-value {
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 0;
  color: #667eea;
  text-shadow: none;
  line-height: 1;
}

.stat-label {
  font-size: 0.75rem;
  opacity: 0.7;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
}

.modal-footer {
  margin-top: 1rem;
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.btn-celebrate {
  background: linear-gradient(45deg, #ff6b6b, #ffa500);
  border: none;
  color: white;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: bold;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 10px 20px rgba(255, 107, 107, 0.3);
  animation: buttonPulse 2s ease-in-out infinite;
}

@keyframes buttonPulse {
  0%, 100% { transform: scale(1); box-shadow: 0 10px 20px rgba(255, 107, 107, 0.3); }
  50% { transform: scale(1.05); box-shadow: 0 15px 30px rgba(255, 107, 107, 0.5); }
}

.btn-celebrate:hover {
  transform: translateY(-2px);
  box-shadow: 0 15px 30px rgba(255, 107, 107, 0.5);
  background: linear-gradient(45deg, #ff5252, #ff9500);
}

.btn-celebrate:active {
  transform: translateY(0);
  box-shadow: 0 8px 16px rgba(255, 107, 107, 0.4);
}

/* Responsive Design */
@media (max-width: 768px) {
  .streak-achievement-modal {
    padding: 1rem;
    margin: 0.5rem;
    min-height: unset;
    max-height: 95vh;
    overflow-y: auto;
  }
  
  .achievement-title {
    font-size: 1.6rem;
    margin-bottom: 0.2rem;
  }
  
  .streak-subtitle {
    font-size: 0.9rem;
  }
  
  .streak-icon {
    font-size: 2.5rem;
    margin-bottom: 0.4rem;
  }
  
  .streak-number {
    font-size: 2rem;
  }
  
  .streak-number-container {
    padding: 1.2rem;
    margin-right: 0.6rem;
  }
  
  .streak-flame {
    font-size: 2rem;
  }

  .stats-section {
    margin: 0.8rem 0;
    gap: 0.3rem;
  }
  
  .stat-item {
    padding: 0.2rem;
  }
  
  .streak-display {
    margin: 0.4rem 0;
  }
  
  .achievement-message {
    margin: 0.4rem 0;
  }
  
  .congrats-text {
    font-size: 1.3rem;
    margin-bottom: 0.4rem;
  }
  
  .motivational-text {
    font-size: 0.85rem;
  }
  
  .modal-footer {
    margin-top: 0.8rem;
  }
  
  .btn-celebrate {
    padding: 0.7rem 1.3rem;
    font-size: 0.9rem;
  }
}
</style>